import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, MessageSquareWarning, FileText, Youtube, BarChart3,
  Plus, Loader2, CheckCircle2, X, Wand2, Trash2, Ban, Star, Server, Mail, Send, Brain,
  BookOpen, ThumbsUp, ThumbsDown, FilePlus2, Rocket, Gauge, RefreshCw, Search,
  History, GitCompare, RotateCcw, GitMerge, FlaskConical, Bell, Clock, BellOff,
} from 'lucide-react';
import KbProcedureEditor from './KbProcedureEditor';
import AdminAIChat from '../../components/Assistant/AdminAIChat';
import { toast } from 'sonner';
import api from '../../services/api';

const STATUS_OPTIONS = ['brouillon', 'a_verifier', 'valide', 'publie', 'desactive', 'obsolete'];
const STATUS_COLORS = {
  brouillon: '#6B6B6E', a_verifier: '#FFB800', valide: '#00FFFF',
  publie: '#00FF88', desactive: '#FF3B5C', obsolete: '#FF6B35',
};

const StatCard = ({ label, value, color = '#FF6B35' }) => (
  <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
    <p className="text-3xl font-bold" style={{ color }}>{value}</p>
    <p className="text-[#6B6B6E] text-sm mt-1">{label}</p>
  </div>
);

const linesToArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join('\n');
const csvToArr = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

const CONNECTION_TYPES = ['xtream', 'm3u', 'portal', 'mac'];
const SOURCE_FIELDS = ['server_url', 'smarters_url', 'm3u_link'];

const ProfileModal = ({ initial, onClose, onSaved }) => {
  const isNew = !initial.id;
  const [f, setF] = useState({
    app: initial.app || '', device: initial.device || '',
    connection_type: initial.connection_type || 'xtream',
    supports_xtream: initial.supports_xtream ?? true,
    supports_m3u: initial.supports_m3u ?? true,
    supports_portal: initial.supports_portal ?? false,
    supports_mac: initial.supports_mac ?? false,
    server_url_source: initial.server_url_source || 'server_url',
    required_fields: (initial.required_fields || []).join(', '),
    instructions: arrToLines(initial.instructions),
    priority: initial.priority ?? 5, active: initial.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.app.trim()) { toast.error("Le champ Application est requis"); return; }
    setSaving(true);
    const payload = {
      app: f.app.trim(), device: f.device.trim() || null,
      connection_type: f.connection_type,
      supports_xtream: f.supports_xtream, supports_m3u: f.supports_m3u,
      supports_portal: f.supports_portal, supports_mac: f.supports_mac,
      server_url_source: f.server_url_source,
      required_fields: csvToArr(f.required_fields),
      instructions: linesToArr(f.instructions),
      priority: Number(f.priority) || 5, active: f.active,
    };
    try {
      if (isNew) await api.post('/admin/assistant/connection-profiles', payload);
      else await api.put(`/admin/assistant/connection-profiles/${initial.id}`, payload);
      toast.success('Profil enregistré');
      onSaved();
    } catch (e) { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children }) => (
    <div><label className="block text-xs text-[#B8B8BA] mb-1">{label}</label>{children}</div>
  );
  const inputCls = "w-full bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6B35] focus:outline-none";

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121214] border border-[#3A3A3D] rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#3A3A3D]">
          <h3 className="text-white font-semibold">{isNew ? 'Nouveau profil de connexion' : 'Modifier le profil'}</h3>
          <button onClick={onClose} className="text-[#B8B8BA] hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Application *"><input className={inputCls} value={f.app} onChange={(e) => set('app', e.target.value)} data-testid="profile-app" /></Field>
            <Field label="Appareil (optionnel)"><input className={inputCls} value={f.device} onChange={(e) => set('device', e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type de connexion">
              <select className={inputCls} value={f.connection_type} onChange={(e) => set('connection_type', e.target.value)}>
                {CONNECTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Source de l'URL serveur">
              <select className={inputCls} value={f.server_url_source} onChange={(e) => set('server_url_source', e.target.value)}>
                {SOURCE_FIELDS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex flex-wrap gap-3">
            {['supports_xtream', 'supports_m3u', 'supports_portal', 'supports_mac'].map((k) => (
              <label key={k} className="flex items-center gap-1.5 text-xs text-[#B8B8BA]">
                <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} /> {k.replace('supports_', '')}
              </label>
            ))}
          </div>
          <Field label="Champs à afficher (séparés par des virgules)"><input className={inputCls} value={f.required_fields} onChange={(e) => set('required_fields', e.target.value)} placeholder="server_url, username, password" /></Field>
          <Field label="Instructions (une par ligne)"><textarea rows={5} className={inputCls} value={f.instructions} onChange={(e) => set('instructions', e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priorité"><input type="number" className={inputCls} value={f.priority} onChange={(e) => set('priority', e.target.value)} /></Field>
            <label className="flex items-center gap-2 text-sm text-[#B8B8BA] mt-6"><input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Actif</label>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-[#3A3A3D]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#B8B8BA] hover:text-white">Annuler</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-[#FF6B35] text-white rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="save-profile-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const ArticleModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({
    title: '', category: '', subcategory: '', devices: '', apps: '', connection_type: '',
    short_answer: '', detailed_answer: '', steps: '', keywords: '', youtube_queries: '',
    difficulty: 'facile', status: 'a_verifier', main_question: '', ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title) { toast.error('Titre requis'); return; }
    setSaving(true);
    const payload = {
      title: f.title, main_question: f.main_question, category: f.category, subcategory: f.subcategory,
      devices: csvToArr(f.devices), apps: csvToArr(f.apps), connection_type: f.connection_type,
      short_answer: f.short_answer, detailed_answer: f.detailed_answer,
      steps: linesToArr(f.steps), keywords: csvToArr(f.keywords),
      youtube_queries: linesToArr(f.youtube_queries), difficulty: f.difficulty, status: f.status,
    };
    try {
      if (f.id) await api.put(`/admin/assistant/articles/${f.id}`, payload);
      else await api.post('/admin/assistant/articles', payload);
      toast.success('Fiche enregistrée');
      onSaved();
    } catch (e) { toast.error('Erreur enregistrement'); }
    finally { setSaving(false); }
  };

  const field = (label, k, textarea = false, ph = '') => (
    <div>
      <label className="text-xs text-[#B8B8BA] mb-1 block">{label}</label>
      {textarea
        ? <textarea value={f[k] || ''} onChange={set(k)} rows={3} placeholder={ph}
            className="w-full bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6B35] focus:outline-none" />
        : <input value={f[k] || ''} onChange={set(k)} placeholder={ph}
            className="w-full bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6B35] focus:outline-none" />}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121214] border border-[#3A3A3D] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#3A3A3D] sticky top-0 bg-[#121214]">
          <h3 className="text-white font-bold">{f.id ? 'Modifier la fiche' : 'Nouvelle fiche'}</h3>
          <button onClick={onClose} className="text-[#B8B8BA] hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {field('Titre *', 'title')}
          {field('Question principale', 'main_question')}
          <div className="grid grid-cols-2 gap-3">
            {field('Catégorie', 'category')}
            {field('Sous-catégorie', 'subcategory')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field('Appareils (séparés par ,)', 'devices', false, 'Fire TV Stick, Android TV')}
            {field('Applications (séparées par ,)', 'apps', false, 'TiviMate, IPTV Smarters Pro')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field('Type de connexion', 'connection_type', false, 'xtream / m3u / mac / portal')}
            {field('Mots-clés (séparés par ,)', 'keywords')}
          </div>
          {field('Réponse courte', 'short_answer', true)}
          {field('Réponse détaillée', 'detailed_answer', true)}
          {field('Étapes (une par ligne)', 'steps', true, '1ère étape\n2e étape')}
          {field('Requêtes YouTube (une par ligne)', 'youtube_queries', true)}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#B8B8BA] mb-1 block">Difficulté</label>
              <select value={f.difficulty} onChange={set('difficulty')} className="w-full bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white">
                {['facile', 'moyen', 'avancé'].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#B8B8BA] mb-1 block">Statut</label>
              <select value={f.status} onChange={set('status')} className="w-full bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[#3A3A3D] flex justify-end gap-2 sticky bottom-0 bg-[#121214]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#B8B8BA] hover:text-white">Annuler</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-[#FF6B35] text-white rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="save-article-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const AssistantAdmin = () => {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [unresolved, setUnresolved] = useState([]);
  const [videos, setVideos] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [setupData, setSetupData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [videoPerf, setVideoPerf] = useState(null);
  const [videoFilters, setVideoFilters] = useState({ device: '', app: '', problem: '', status: '', min_resolution: '' });
  const [researched, setResearched] = useState(null);
  const [researchStatus, setResearchStatus] = useState('');
  const [profileModal, setProfileModal] = useState(null);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStats = useCallback(() => Promise.all([
    api.get('/admin/assistant/stats'),
    api.get('/admin/assistant/page-stats'),
  ]).then(([s, p]) => setStats({ ...s.data, page_stats: p.data })).catch(() => {}), []);
  const loadArticles = useCallback(() => api.get('/admin/assistant/articles').then((r) => setArticles(r.data.items)).catch(() => {}), []);
  const loadUnresolved = useCallback(() => api.get('/admin/assistant/unresolved').then((r) => setUnresolved(r.data.items)).catch(() => {}), []);
  const loadVideos = useCallback(() => api.get('/admin/assistant/videos').then((r) => setVideos(r.data.items)).catch(() => {}), []);
  const loadProfiles = useCallback(() => api.get('/admin/assistant/connection-profiles').then((r) => setProfiles(r.data.items)).catch(() => {}), []);
  const [learning, setLearning] = useState({ requests: [], suggestions: [], stats: null });
  const loadLearning = useCallback(() => {
    Promise.all([
      api.get('/admin/assistant/knowledge-requests'),
      api.get('/admin/assistant/enrichment-suggestions'),
      api.get('/admin/assistant/learning-stats'),
    ]).then(([kr, sug, st]) => setLearning({
      requests: kr.data.items || [], suggestions: sug.data.items || [], stats: st.data,
    })).catch(() => {});
  }, []);
  const krAction = async (id, status) => {
    try { await api.put(`/admin/assistant/knowledge-requests/${id}`, { status }); toast.success('Mis à jour'); loadLearning(); }
    catch (e) { toast.error('Erreur'); }
  };
  const sugAction = async (id, status) => {
    try { await api.put(`/admin/assistant/enrichment-suggestions/${id}`, { status }); toast.success('Mis à jour'); loadLearning(); }
    catch (e) { toast.error('Erreur'); }
  };
  // Jalon amélioration continue — re-scan / fusion / brouillon
  const [rescanReport, setRescanReport] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const [selectedSug, setSelectedSug] = useState([]);
  const runRescan = async () => {
    setRescanning(true);
    try {
      const r = await api.post('/admin/assistant/enrichment-suggestions/rescan');
      setRescanReport(r.data);
      toast.success(`Analyse terminée : ${r.data.created} créées`);
      loadLearning();
    } catch (e) {
      if (e.response?.status === 409) toast.error('Une analyse est déjà en cours.');
      else toast.error('Erreur');
    } finally { setRescanning(false); }
  };
  const toggleSelectSug = (id) => setSelectedSug((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const mergeSelected = async () => {
    try {
      const r = await api.post('/admin/assistant/enrichment-suggestions/merge', { ids: selectedSug });
      toast.success(`Fusionnées (${r.data.merged_count})`); setSelectedSug([]); loadLearning();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fusion impossible'); }
  };
  const genDraft = async (id) => {
    try { await api.post(`/admin/assistant/enrichment-suggestions/${id}/generate-draft`); toast.success('Brouillon créé'); loadLearning(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Flux guidé suggestion → publication (parcours unique)
  const [guided, setGuided] = useState(null); // {sug, step, ctx, draft, diff, sim}
  const openGuided = async (sug) => {
    setGuided({ sug, step: 1, loading: true });
    try {
      const r = await api.get(`/admin/assistant/enrichment-suggestions/${sug.id}/context`);
      setGuided({ sug, step: 1, ctx: r.data });
    } catch (e) { setGuided({ sug, step: 1, ctx: null }); }
  };
  const guidedGenerate = async () => {
    setGuided((g) => ({ ...g, loading: true }));
    try {
      const r = await api.post(`/admin/assistant/enrichment-suggestions/${guided.sug.id}/generate-draft`);
      setGuided((g) => ({ ...g, step: 3, draft: r.data }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Génération impossible'); setGuided((g) => ({ ...g, loading: false })); }
  };
  const guidedPublish = async () => {
    // Publie le brouillon procédure/fiche créé, en liant la suggestion source
    const draft = guided.draft || {};
    let action = null;
    if (draft.procedure?.slug) action = { type: 'create_procedure_draft', mode: 'publish', source_suggestion_id: guided.sug.id, procedure: { slug: draft.procedure.slug } };
    if (!action) { toast.error('Publication guidée : finalisez le brouillon dans l\'éditeur puis publiez.'); return; }
    try {
      await api.post('/admin/assistant/ai-chat/apply', { action });
      toast.success('Publié (versionné) — suggestion marquée publiée'); setGuided(null); loadLearning();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publication impossible'); }
  };

  // Jalon — Historique des versions
  const [versions, setVersions] = useState([]);
  const [verFilter, setVerFilter] = useState({ object_type: '' });
  const [verDiff, setVerDiff] = useState(null);
  const [verPick, setVerPick] = useState({ ref: null, object_type: null, v1: null, v2: null });
  const [verStats, setVerStats] = useState(null);
  const loadVersions = useCallback(() => {
    const qs = verFilter.object_type ? `?object_type=${verFilter.object_type}` : '';
    api.get(`/admin/assistant/versions${qs}`).then((r) => setVersions(r.data.items || [])).catch(() => {});
  }, [verFilter]);
  const runDiff = async () => {
    const { object_type, ref, v1, v2 } = verPick;
    if (!ref || !v1 || !v2) { toast.error('Choisissez deux versions du même objet'); return; }
    try {
      const r = await api.get(`/admin/assistant/versions/diff?object_type=${object_type}&ref=${ref}&v1=${v1}&v2=${v2}`);
      setVerDiff(r.data);
    } catch (e) { toast.error('Diff impossible'); }
  };
  const restoreVersion = async (object_type, ref, version) => {
    if (!window.confirm(`Restaurer la version ${version} ? Une NOUVELLE version sera créée (l'ancienne n'est pas réactivée directement).`)) return;
    try {
      const r = await api.post('/admin/assistant/versions/restore', { object_type, ref, version, confirm: true });
      toast.success(`Restaurée → nouvelle version ${r.data.new_version}`); loadVersions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const loadVerStats = async (object_type, ref, version) => {
    try { const r = await api.get(`/admin/assistant/versions/stats?object_type=${object_type}&ref=${ref}&version=${version}`); setVerStats(r.data); }
    catch (e) { toast.error('Stats indisponibles'); }
  };

  // Jalon finalisation — Alertes de dégradation
  const [alerts, setAlerts] = useState([]);
  const [scanReport, setScanReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [degJob, setDegJob] = useState(null);
  const [degPrefs, setDegPrefs] = useState(null);
  const [degNotifs, setDegNotifs] = useState([]);
  const loadAlerts = useCallback(() => {
    api.get('/admin/assistant/degradation/alerts?status=active').then((r) => setAlerts(r.data.items || [])).catch(() => {});
    api.get('/admin/assistant/degradation/job').then((r) => setDegJob(r.data)).catch(() => {});
    api.get('/admin/assistant/degradation/prefs').then((r) => setDegPrefs(r.data)).catch(() => {});
    api.get('/admin/assistant/notifications?status=unread').then((r) => setDegNotifs(r.data.items || [])).catch(() => {});
  }, []);
  const runDegradationScan = async () => {
    setScanning(true);
    try { const r = await api.post('/admin/assistant/degradation/scan'); setScanReport(r.data); toast.success(`Analyse : ${r.data.alerts_created} alerte(s)`); loadAlerts(); }
    catch (e) { toast.error('Erreur'); } finally { setScanning(false); }
  };
  const savePrefs = async (patch) => {
    try { const r = await api.put('/admin/assistant/degradation/prefs', patch); setDegPrefs(r.data); toast.success('Préférences enregistrées'); }
    catch (e) { toast.error('Erreur'); }
  };
  const markNotifRead = async (id) => {
    try { await api.put(`/admin/assistant/notifications/${id}`, { status: 'read' }); setDegNotifs((n) => n.filter((x) => x.id !== id)); }
    catch (e) { toast.error('Erreur'); }
  };
  const updateAlert = async (id, status) => {
    try { await api.put(`/admin/assistant/degradation/alerts/${id}`, { status }); toast.success('Alerte mise à jour'); loadAlerts(); }
    catch (e) { toast.error('Erreur'); }
  };
  const restoreFromAlert = (a) => restoreVersion('procedure', a.procedure_slug, a.version);

  // Phase 4.3-E — administration des procédures & dépannages
  const [procedures, setProcedures] = useState([]);
  const [editorSlug, setEditorSlug] = useState(null);
  const [resolvedTs, setResolvedTs] = useState([]);
  const [unresolvedTs, setUnresolvedTs] = useState([]);
  const loadProcedures = useCallback(() => api.get('/admin/assistant/procedures').then((r) => setProcedures(r.data.items || [])).catch(() => {}), []);
  const loadResolvedTs = useCallback(() => api.get('/admin/assistant/troubleshootings?kind=resolved&limit=50').then((r) => setResolvedTs(r.data.items || [])).catch(() => {}), []);
  const loadUnresolvedTs = useCallback(() => api.get('/admin/assistant/troubleshootings?kind=unresolved&limit=50').then((r) => setUnresolvedTs(r.data.items || [])).catch(() => {}), []);
  const procStatus = async (slug, status) => {
    try {
      await api.post(`/admin/assistant/procedures/${slug}/status`, { status });
      toast.success(status === 'publie' ? 'Publiée' : 'Statut mis à jour'); loadProcedures();
    } catch (e) {
      if (e.response?.status === 422) toast.error('Procédure invalide — ouvrez l\'éditeur pour corriger');
      else toast.error('Erreur');
    }
  };
  const deleteProc = async (slug) => {
    if (!window.confirm('Supprimer cette procédure ?')) return;
    try { await api.delete(`/admin/assistant/procedures/${slug}`); toast.success('Supprimée'); loadProcedures(); }
    catch (e) { toast.error('Erreur'); }
  };
  const newProc = async () => {
    try {
      const r = await api.post('/admin/assistant/procedures', { title: 'Nouvelle procédure' });
      setEditorSlug(r.data.slug);
    } catch (e) { toast.error('Erreur'); }
  };
  const procFromSuggestion = async (id) => {
    try { const r = await api.post(`/admin/assistant/procedures/from-suggestion/${id}`); toast.success('Brouillon créé'); setEditorSlug(r.data.slug); }
    catch (e) { toast.error('Erreur'); }
  };
  const procFromSession = async (id) => {
    try { const r = await api.post(`/admin/assistant/procedures/from-session/${id}`); toast.success('Brouillon créé'); setEditorSlug(r.data.slug); }
    catch (e) { toast.error('Erreur'); }
  };
  const loadSetup = useCallback(() => {
    Promise.all([
      api.get('/admin/assistant/setup-reminders'),
      api.get('/admin/assistant/cron-status'),
      api.get('/admin/assistant/setup-metrics?range_days=30&group=day'),
    ]).then(([r, c, m]) => setSetupData({ ...r.data, cron: c.data, metrics: m.data })).catch(() => {});
  }, []);
  const [testEmail, setTestEmail] = useState('');
  const loadCampaign = useCallback(() => {
    Promise.all([
      api.get('/admin/assistant/waiting-campaign?limit=50'),
      api.get('/admin/assistant/waiting-campaign/cron-status'),
    ]).then(([r, c]) => setCampaignData({ ...r.data, cron: c.data })).catch(() => {});
  }, []);
  const runCampaign = async () => {
    try { const r = await api.post('/admin/assistant/waiting-campaign/run', {}); toast.success(`Campagne : ${r.data.sent} envoyée(s), ${r.data.already_sent} déjà, ${r.data.skipped_recent} récentes`); loadCampaign(); }
    catch (e) { toast.error('Erreur exécution campagne'); }
  };
  const campaignCancel = async (id) => {
    try { await api.post(`/admin/assistant/waiting-campaign/${id}/cancel`); toast.success('Annulée'); loadCampaign(); }
    catch (e) { toast.error('Erreur'); }
  };
  const loadVideoPerf = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(videoFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
    api.get(`/admin/assistant/video-performance?limit=50&${params.toString()}`)
      .then((r) => setVideoPerf(r.data)).catch(() => {});
  }, [videoFilters]);
  const videoPerfAction = async (videoId, action, contextKey) => {
    try { const r = await api.post(`/admin/assistant/video-performance/${videoId}/action`, { action, context_key: contextKey });
      toast.success(r.data.availability ? `Disponibilité : ${r.data.availability.available ? 'OK' : 'indisponible'}` : 'Fait'); loadVideoPerf(); }
    catch (e) { toast.error('Erreur'); }
  };
  const loadResearched = useCallback(() => {
    const s = researchStatus ? `?status=${researchStatus}` : '';
    Promise.all([
      api.get(`/admin/assistant/researched-answers${s}`),
      api.get('/admin/assistant/researched-answers/web-status'),
    ]).then(([r, w]) => setResearched({ ...r.data, web: w.data })).catch(() => {});
  }, [researchStatus]);
  const researchAction = async (id, action, extra = {}) => {
    try { await api.post(`/admin/assistant/researched-answers/${id}`, { action, ...extra }); toast.success('Fait'); loadResearched(); }
    catch (e) { toast.error('Erreur'); }
  };
  const researchPublish = async (id) => {
    try { await api.post(`/admin/assistant/researched-answers/${id}/publish`); toast.success('Publié dans la base de connaissances'); loadResearched(); }
    catch (e) { toast.error('Erreur publication'); }
  };
  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Saisissez une adresse'); return; }
    try { const r = await api.post('/admin/assistant/setup-reminders/test-email', { email: testEmail }); toast.success(`Test : ${r.data.status}${r.data.message_id ? ' (' + r.data.message_id.slice(0, 24) + '…)' : ''}`); }
    catch (e) { toast.error('Erreur envoi test'); }
  };

  useEffect(() => {
    loadStats();
    if (tab === 'articles') loadArticles();
    if (tab === 'unresolved') loadUnresolved();
    if (tab === 'videos') loadVideos();
    if (tab === 'profiles') loadProfiles();
    if (tab === 'setup') loadSetup();
    if (tab === 'campaign') loadCampaign();
    if (tab === 'video-perf') loadVideoPerf();
    if (tab === 'researched') loadResearched();
    if (tab === 'learning') loadLearning();
    if (tab === 'versions') loadVersions();
    if (tab === 'degradations') loadAlerts();
    if (tab === 'procedures') loadProcedures();
    if (tab === 'resolved') loadResolvedTs();
    if (tab === 'unresolved-ts') loadUnresolvedTs();
  }, [tab, loadStats, loadArticles, loadUnresolved, loadVideos, loadProfiles, loadSetup, loadCampaign, loadVideoPerf, loadResearched, loadLearning, loadProcedures, loadResolvedTs, loadUnresolvedTs, loadVersions, loadAlerts]);

  const setupResend = async (id) => {
    try { const r = await api.post(`/admin/assistant/setup-reminders/${id}/resend`); toast.success(`Relance : ${r.data.status}`); loadSetup(); }
    catch (e) { toast.error('Erreur'); }
  };
  const setupCancel = async (id) => {
    try { await api.post(`/admin/assistant/setup-reminders/${id}/cancel`); toast.success('Annulée'); loadSetup(); }
    catch (e) { toast.error('Erreur'); }
  };

  const draftFromUnresolved = async (u) => {
    setLoading(true);
    try {
      const r = await api.post(`/admin/assistant/unresolved/${u.id}/draft-article`);
      const d = r.data.draft || {};
      setModal({
        unresolvedId: u.id,
        title: d.title || u.summary || u.original_question,
        main_question: d.main_question || u.original_question,
        category: d.category || u.category || '', subcategory: d.subcategory || u.subcategory || '',
        devices: (d.devices || (u.device ? [u.device] : [])).join(', '),
        apps: (d.apps || (u.app ? [u.app] : [])).join(', '),
        connection_type: d.connection_type || u.connection || '',
        short_answer: d.short_answer || '', detailed_answer: d.detailed_answer || '',
        steps: arrToLines(d.steps), keywords: (d.keywords || []).join(', '),
        youtube_queries: arrToLines(d.youtube_queries), difficulty: d.difficulty || 'facile', status: 'a_verifier',
      });
    } catch (e) { toast.error('Erreur génération du brouillon'); }
    finally { setLoading(false); }
  };

  const videoAction = async (videoId, action) => {
    try { await api.post(`/admin/assistant/videos/${videoId}/action`, { action }); toast.success('Fait'); loadVideos(); }
    catch (e) { toast.error('Erreur'); }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'ai-admin', label: 'Assistant IA Admin', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'unresolved', label: 'Questions non résolues', icon: <MessageSquareWarning className="h-4 w-4" /> },
    { id: 'articles', label: 'Fiches', icon: <FileText className="h-4 w-4" /> },
    { id: 'videos', label: 'Vidéos', icon: <Youtube className="h-4 w-4" /> },
    { id: 'video-perf', label: 'Performance des vidéos', icon: <Gauge className="h-4 w-4" /> },
    { id: 'profiles', label: 'Profils de connexion', icon: <Server className="h-4 w-4" /> },
    { id: 'setup', label: "Relances installation", icon: <Mail className="h-4 w-4" /> },
    { id: 'campaign', label: "Campagne Waiting > 7j", icon: <Rocket className="h-4 w-4" /> },
    { id: 'learning', label: 'Suggestions à officialiser', icon: <Brain className="h-4 w-4" /> },
    { id: 'versions', label: 'Versions', icon: <History className="h-4 w-4" /> },
    { id: 'degradations', label: 'Dégradations', icon: <Gauge className="h-4 w-4" /> },
    { id: 'researched', label: 'Réponses IA à valider', icon: <Search className="h-4 w-4" /> },
    { id: 'procedures', label: 'Procédures', icon: <BookOpen className="h-4 w-4" /> },
    { id: 'resolved', label: 'Dépannages résolus', icon: <ThumbsUp className="h-4 w-4" /> },
    { id: 'unresolved-ts', label: 'Dépannages non résolus', icon: <ThumbsDown className="h-4 w-4" /> },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="assistant-admin">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[#FF6B35]" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Assistant IA</h1>
          <p className="text-[#6B6B6E] text-sm">Base de connaissances, vidéos et supervision</p>
        </div>
      </div>

      {stats && !stats.ai_configured && (
        <div className="mb-4 bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 rounded-lg p-3 text-[#FF3B5C] text-sm">
          ⚠️ Le moteur IA n'est pas configuré (EMERGENT_LLM_KEY).
        </div>
      )}
      {stats && !stats.youtube_configured && (
        <div className="mb-4 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 text-[#FFB800] text-sm">
          ℹ️ Recherche YouTube inactive — ajoutez la variable <code>YOUTUBE_API_KEY</code> pour activer les recommandations vidéo automatiques.
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#3A3A3D]">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-[#FF6B35] text-[#FF6B35]' : 'border-transparent text-[#B8B8BA] hover:text-white'}`}
            data-testid={`assistant-tab-${t.id}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'ai-admin' && <AdminAIChat />}

      {tab === 'overview' && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Conversations" value={stats.conversations} />
          <StatCard label="Taux de résolution" value={`${stats.resolution_rate}%`} color="#00FF88" />
          <StatCard label="Questions non résolues" value={stats.unresolved_open} color="#FFB800" />
          <StatCard label="Questions posées" value={stats.questions} color="#00FFFF" />
          <StatCard label="Fiches publiées" value={`${stats.articles_published}/${stats.articles_total}`} color="#00FF88" />
          <StatCard label="Recherches YouTube" value={stats.youtube_searches} color="#FF3B5C" />
          <StatCard label="Recherches sans résultat" value={stats.youtube_no_result} color="#FFB800" />
          <StatCard label="Catégories couvertes" value={Object.keys(stats.categories || {}).length} color="#00FFFF" />
        </div>
      )}

      {tab === 'overview' && stats?.page_stats && (
        <div className="mt-6" data-testid="page-usage-stats">
          <h3 className="text-white text-sm font-semibold mb-3">Usage pages (Téléchargements & Tutoriels et assistance)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Ouvertures Téléchargements" value={stats.page_stats.downloads.opened_total} color="#FF6B35" />
            <StatCard label="…depuis Nova" value={stats.page_stats.downloads.from_nova} color="#FF6B35" />
            <StatCard label="…depuis Tutoriels" value={stats.page_stats.downloads.from_tutoriels} color="#FF6B35" />
            <StatCard label="Apps non trouvées" value={stats.page_stats.downloads.app_not_found_declared} color="#FF3B5C" />
            <StatCard label="Ouvertures Tutoriels+assist." value={stats.page_stats.tutoriels.opened} color="#00FFFF" />
            <StatCard label="Parcours Nova lancés" value={stats.page_stats.tutoriels.nova_journeys_started} color="#00FF88" />
            <StatCard label="Problèmes code déclarés" value={stats.page_stats.downloads.code_problem_declared} color="#FFB800" />
            <StatCard label="Installations OK déclarées" value={stats.page_stats.downloads.install_success_declared} color="#00FF88" />
          </div>
        </div>
      )}

      {/* Unresolved */}
      {tab === 'unresolved' && (
        <div className="space-y-3">
          {unresolved.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucune question non résolue. 🎉</p>}
          {unresolved.map((u) => (
            <div key={u.id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4" data-testid="unresolved-item">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{u.summary || u.original_question}</p>
                  <p className="text-[#6B6B6E] text-xs mt-1">« {u.original_question} »</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {u.device && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00FFFF]/10 text-[#00FFFF]">{u.device}</span>}
                    {u.app && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35]">{u.app}</span>}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#222226] text-[#B8B8BA]">{u.failure_reason}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#222226] text-[#B8B8BA]">{u.status}</span>
                  </div>
                </div>
                <button onClick={() => draftFromUnresolved(u)} disabled={loading}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-[#FF6B35]/15 text-[#FF6B35] hover:bg-[#FF6B35]/25 transition-colors"
                  data-testid="draft-article-btn">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  Générer un brouillon IA
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Articles */}
      {tab === 'articles' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setModal({})} className="flex items-center gap-2 px-4 py-2 text-sm bg-[#FF6B35] text-white rounded-lg" data-testid="new-article-btn">
              <Plus className="h-4 w-4" /> Nouvelle fiche
            </button>
          </div>
          <div className="space-y-2">
            {articles.map((a) => (
              <div key={a.id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{a.title}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${STATUS_COLORS[a.status]}20`, color: STATUS_COLORS[a.status] }}>{a.status}</span>
                    {a.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#222226] text-[#B8B8BA]">{a.category}</span>}
                    <span className="text-[10px] text-[#6B6B6E]">👁 {a.views} · ✓ {a.resolutions} · ✗ {a.failures}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setModal({
                    id: a.id, title: a.title, main_question: a.main_question, category: a.category || '',
                    subcategory: a.subcategory || '', devices: (a.devices || []).join(', '), apps: (a.apps || []).join(', '),
                    connection_type: a.connection_type || '', short_answer: a.short_answer || '', detailed_answer: a.detailed_answer || '',
                    steps: arrToLines(a.steps), keywords: (a.keywords || []).join(', '), youtube_queries: arrToLines(a.youtube_queries),
                    difficulty: a.difficulty || 'facile', status: a.status,
                  })} className="text-xs px-3 py-1.5 rounded-lg bg-[#222226] text-white hover:bg-[#2A2A2D]" data-testid="edit-article-btn">Modifier</button>
                  <button onClick={async () => { if (window.confirm('Supprimer cette fiche ?')) { await api.delete(`/admin/assistant/articles/${a.id}`); toast.success('Supprimée'); loadArticles(); } }}
                    className="text-[#FF3B5C] hover:bg-[#FF3B5C]/10 p-2 rounded-lg"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {tab === 'videos' && (
        <div className="space-y-2">
          {videos.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucune vidéo proposée pour l'instant (recherche YouTube requise).</p>}
          {videos.map((v) => (
            <div key={v.id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-3 flex items-center gap-3">
              {v.thumbnail && <img src={v.thumbnail} alt="" className="w-24 h-14 object-cover rounded-lg flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{v.title}</p>
                <p className="text-[#6B6B6E] text-xs">{v.channel_title} · statut: {v.status}</p>
                <p className="text-[10px] text-[#6B6B6E]">proposée {v.stats?.proposed || 0}× · ✓ {v.stats?.resolved || 0} · 👎 {v.stats?.negative || 0}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => videoAction(v.video_id, 'favor')} className="p-2 rounded-lg text-[#00FF88] hover:bg-[#00FF88]/10" title="Favoriser"><Star className="h-4 w-4" /></button>
                <button onClick={() => videoAction(v.video_id, 'exclude')} className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Exclure"><Ban className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profils de connexion */}
      {tab === 'profiles' && (
        <div className="space-y-3" data-testid="profiles-panel">
          <div className="flex justify-between items-center">
            <p className="text-[#6B6B6E] text-sm">Définissez, par application, quelles informations afficher et comment connecter le client. Aucune donnée sensible ici.</p>
            <button onClick={() => setProfileModal({})} className="flex items-center gap-2 px-4 py-2 text-sm bg-[#FF6B35] text-white rounded-lg flex-shrink-0" data-testid="new-profile-btn">
              <Plus className="h-4 w-4" /> Nouveau profil
            </button>
          </div>
          {profiles.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucun profil.</p>}
          {profiles.map((p) => (
            <div key={p.id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-3 flex items-start gap-3" data-testid="profile-row">
              <Server className="h-5 w-5 text-[#FF6B35] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{p.app}{p.device ? ` · ${p.device}` : ''}
                  {!p.active && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF3B5C]/15 text-[#FF3B5C]">inactif</span>}
                </p>
                <p className="text-[#6B6B6E] text-xs">Connexion: {p.connection_type} · source URL: {p.server_url_source} · champs: {(p.required_fields || []).join(', ')}</p>
                <p className="text-[10px] text-[#6B6B6E] mt-0.5">{(p.instructions || []).length} instruction(s) · priorité {p.priority}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setProfileModal(p)} className="p-2 rounded-lg text-[#00FFFF] hover:bg-[#00FFFF]/10" title="Modifier" data-testid="edit-profile-btn"><Wand2 className="h-4 w-4" /></button>
                <button onClick={async () => { if (window.confirm('Supprimer ce profil ?')) { await api.delete(`/admin/assistant/connection-profiles/${p.id}`); toast.success('Supprimé'); loadProfiles(); } }}
                  className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Relances installation */}
      {tab === 'setup' && setupData && (
        <div className="space-y-4" data-testid="setup-reminders-panel">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Non configurés" value={setupData.stats.detected_unactivated} color="#FFB800" />
            <StatCard label="E-mails envoyés" value={setupData.stats.emails_sent} color="#00FFFF" />
            <StatCard label="Délivrés/OK" value={setupData.stats.emails_sent - setupData.stats.emails_failed} color="#00FF88" />
            <StatCard label="Échecs" value={setupData.stats.emails_failed} color="#FF3B5C" />
            <StatCard label="Activés après relance" value={setupData.stats.converted} color="#00FF88" />
            <StatCard label="Taux conversion" value={`${setupData.stats.conversion_rate}%`} color="#FF6B35" />
          </div>

          {/* Supervision du cron + test Mailgun */}
          {setupData.cron && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4" data-testid="cron-status-panel">
                <p className="text-white text-sm font-semibold mb-2">Supervision du cron</p>
                <div className="text-xs text-[#B8B8BA] space-y-1">
                  <p>Fuseau : <span className="text-white">{setupData.cron.timezone}</span></p>
                  <p>Planification : <span className="text-white">{setupData.cron.schedule}</span></p>
                  <p>Activé : <span className={setupData.cron.enabled ? 'text-[#00FF88]' : 'text-[#FF3B5C]'}>{setupData.cron.enabled ? 'Oui' : 'Non'}</span></p>
                  <p>Verrou actif : <span className="text-white">{setupData.cron.lock_active ? 'Oui' : 'Non'}</span></p>
                  {setupData.cron.last_run
                    ? <p>Dernière exécution : <span className="text-white">{setupData.cron.last_run.finished_at ? new Date(setupData.cron.last_run.finished_at).toLocaleString('fr-FR') : '—'}</span> ({setupData.cron.last_run.duration_sec ?? '—'}s, {setupData.cron.last_run.sent ?? 0} e-mails, {setupData.cron.last_run.notifications_created ?? 0} notifs)</p>
                    : <p className="text-[#6B6B6E]">Aucune exécution enregistrée pour l'instant.</p>}
                </div>
              </div>
              <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
                <p className="text-white text-sm font-semibold mb-2">Test Mailgun (adresse interne)</p>
                <p className="text-[11px] text-[#6B6B6E] mb-2">Envoie le modèle réel sans créer de relance client ni modifier de statut. Aucun identifiant inclus.</p>
                <div className="flex gap-2">
                  <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="admin@iptv4kfr.fr" className="flex-1 bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6B35] focus:outline-none" data-testid="test-email-input" />
                  <button onClick={sendTestEmail} className="px-3 py-2 text-sm bg-[#FF6B35] text-white rounded-lg flex items-center gap-1.5" data-testid="test-email-btn"><Send className="h-4 w-4" /> Envoyer</button>
                </div>
              </div>
            </div>
          )}

          {/* Graphique de conversion (barres simples) */}
          {setupData.metrics && setupData.metrics.series && setupData.metrics.series.length > 0 && (
            <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4" data-testid="conversion-chart">
              <p className="text-white text-sm font-semibold mb-1">Conversion des relances (30 jours)</p>
              <p className="text-[10px] text-[#6B6B6E] mb-3">{setupData.metrics.note}</p>
              <div className="flex items-end gap-3 h-32">
                {setupData.metrics.series.slice(-14).map((s) => {
                  const max = Math.max(1, ...setupData.metrics.series.map((x) => x.reminders_sent));
                  return (
                    <div key={s.period} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex items-end justify-center gap-0.5 h-24">
                        <div className="w-2 bg-[#00FFFF] rounded-t" style={{ height: `${(s.reminders_sent / max) * 100}%` }} title={`Relances: ${s.reminders_sent}`} />
                        <div className="w-2 bg-[#00FF88] rounded-t" style={{ height: `${(s.activations / max) * 100}%` }} title={`Activations: ${s.activations}`} />
                      </div>
                      <span className="text-[9px] text-[#6B6B6E] rotate-45 origin-left whitespace-nowrap">{s.period.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 text-[10px] text-[#B8B8BA]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#00FFFF] rounded" /> Relances envoyées</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#00FF88] rounded" /> Activations</span>
                <span>Toujours en Waiting : <strong className="text-white">{setupData.metrics.still_waiting}</strong></span>
              </div>
            </div>
          )}

          <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#111113] text-[#6B6B6E]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Statut</th>
                  <th className="text-left px-3 py-2 font-medium">Envoyé le</th>
                  <th className="text-right px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {setupData.items.length === 0 && (
                  <tr><td colSpan="5" className="px-3 py-6 text-center text-[#6B6B6E]">Aucune relance enregistrée.</td></tr>
                )}
                {setupData.items.map((r) => (
                  <tr key={r.id} className="border-t border-[#3A3A3D]" data-testid="setup-reminder-row">
                    <td className="px-3 py-2 text-[#E8E8E8]">{r.user_email || r.user_id}</td>
                    <td className="px-3 py-2 text-[#B8B8BA]">{r.reminder_type}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                        r.status === 'converted' ? 'bg-[#00FF88]/15 text-[#00FF88]' :
                        r.status === 'failed' ? 'bg-[#FF3B5C]/15 text-[#FF3B5C]' :
                        r.status === 'cancelled' ? 'bg-[#6B6B6E]/15 text-[#6B6B6E]' :
                        'bg-[#00FFFF]/15 text-[#00FFFF]'}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-[#6B6B6E] text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString('fr-FR') : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setupResend(r.id)} className="p-2 rounded-lg text-[#00FFFF] hover:bg-[#00FFFF]/10" title="Relancer" data-testid="setup-resend-btn"><Send className="h-4 w-4" /></button>
                        <button onClick={() => setupCancel(r.id)} className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Annuler" data-testid="setup-cancel-btn"><Ban className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'campaign' && campaignData && (
        <div className="space-y-4" data-testid="waiting-campaign-panel">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Campagnes" value={campaignData.stats.campaigns} color="#00FFFF" />
            <StatCard label="E-mails envoyés" value={campaignData.stats.emails_sent} color="#00FFFF" />
            <StatCard label="Notifications" value={campaignData.stats.notifications_created} color="#FFB800" />
            <StatCard label="Nova ouvert" value={campaignData.stats.nova_opened} color="#FF6B35" />
            <StatCard label="Activations" value={campaignData.stats.activations} color="#00FF88" />
            <StatCard label="Taux conversion" value={`${campaignData.stats.conversion_rate}%`} color="#00FF88" />
            <StatCard label="Appareils choisis" value={campaignData.stats.devices_selected} color="#00FFFF" />
            <StatCard label="Apps choisies" value={campaignData.stats.apps_selected} color="#00FFFF" />
            <StatCard label="Tests de lecture" value={campaignData.stats.playback_tests} color="#FFB800" />
            <StatCard label="Délai moyen (h)" value={campaignData.stats.avg_delay_hours ?? '—'} color="#FF6B35" />
            <StatCard label="Toujours en Waiting" value={campaignData.stats.still_waiting} color="#FFB800" />
            <StatCard label="Non activés (total)" value={campaignData.stats.unactivated_total} color="#FF3B5C" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {campaignData.cron && (
              <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4" data-testid="campaign-cron-panel">
                <p className="text-white text-sm font-semibold mb-2">Supervision du cron</p>
                <div className="text-xs text-[#B8B8BA] space-y-1">
                  <p>Fuseau : <span className="text-white">{campaignData.cron.timezone}</span></p>
                  <p>Planification : <span className="text-white">{campaignData.cron.schedule}</span></p>
                  <p>Seuil : <span className="text-white">{campaignData.cron.min_days} jours</span></p>
                  <p>Activé : <span className={campaignData.cron.enabled ? 'text-[#00FF88]' : 'text-[#FF3B5C]'}>{campaignData.cron.enabled ? 'Oui' : 'Non'}</span></p>
                  {campaignData.cron.last_run
                    ? <p>Dernière exécution : <span className="text-white">{campaignData.cron.last_run.sent ?? 0} envoyée(s)</span></p>
                    : <p className="text-[#6B6B6E]">Aucune exécution enregistrée.</p>}
                </div>
              </div>
            )}
            <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4">
              <p className="text-white text-sm font-semibold mb-2">Comparaison des canaux (conversions)</p>
              <div className="text-xs text-[#B8B8BA] space-y-1">
                <p>E-mail : <span className="text-white">{campaignData.stats.channels?.email ?? 0}</span></p>
                <p>Notification : <span className="text-white">{campaignData.stats.channels?.notification ?? 0}</span></p>
                <p>Bulle : <span className="text-white">{campaignData.stats.channels?.bubble ?? 0}</span></p>
              </div>
              <button onClick={runCampaign} className="mt-3 px-3 py-2 text-sm bg-[#FF6B35] text-white rounded-lg flex items-center gap-1.5" data-testid="campaign-run-btn"><Rocket className="h-4 w-4" /> Exécuter la campagne maintenant</button>
              <p className="text-[10px] text-[#6B6B6E] mt-1">Envoie une seule relance par abonnement éligible (Waiting &gt; 7 jours). Idempotent.</p>
            </div>
          </div>

          <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#111113] text-[#6B6B6E]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-left px-3 py-2 font-medium">Appareil</th>
                  <th className="text-left px-3 py-2 font-medium">Statut</th>
                  <th className="text-left px-3 py-2 font-medium">Envoyé le</th>
                  <th className="text-right px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaignData.items.length === 0 && (
                  <tr><td colSpan="5" className="px-3 py-6 text-center text-[#6B6B6E]">Aucune campagne enregistrée.</td></tr>
                )}
                {campaignData.items.map((c) => (
                  <tr key={c.id} className="border-t border-[#3A3A3D]" data-testid="campaign-row">
                    <td className="px-3 py-2 text-[#E8E8E8]">{c.user_email || c.user_id}</td>
                    <td className="px-3 py-2 text-[#B8B8BA]">{c.device_label || c.device_family}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                        c.status === 'converted' ? 'bg-[#00FF88]/15 text-[#00FF88]' :
                        c.status === 'dismissed' ? 'bg-[#6B6B6E]/15 text-[#6B6B6E]' :
                        c.status === 'opened_nova' ? 'bg-[#FF6B35]/15 text-[#FF6B35]' :
                        'bg-[#00FFFF]/15 text-[#00FFFF]'}`}>{c.status}</span>
                    </td>
                    <td className="px-3 py-2 text-[#6B6B6E] text-xs">{c.sent_at ? new Date(c.sent_at).toLocaleString('fr-FR') : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => campaignCancel(c.id)} className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Annuler" data-testid="campaign-cancel-btn"><Ban className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'video-perf' && (
        <div className="space-y-4" data-testid="video-performance-panel">
          <div className="flex flex-wrap gap-2 items-end bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-3">
            {['device', 'app', 'problem'].map((k) => (
              <div key={k}>
                <label className="block text-[10px] text-[#6B6B6E] mb-1">{k === 'device' ? 'Appareil' : k === 'app' ? 'Application' : 'Problème'}</label>
                <input value={videoFilters[k]} onChange={(e) => setVideoFilters((f) => ({ ...f, [k]: e.target.value }))}
                  className="bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-[#FF6B35] focus:outline-none w-36"
                  data-testid={`vperf-filter-${k}`} placeholder="Tous" />
              </div>
            ))}
            <div>
              <label className="block text-[10px] text-[#6B6B6E] mb-1">Statut</label>
              <select value={videoFilters.status} onChange={(e) => setVideoFilters((f) => ({ ...f, status: e.target.value }))}
                className="bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-[#FF6B35] focus:outline-none" data-testid="vperf-filter-status">
                <option value="">Tous</option>
                <option value="proposee">Proposée</option>
                <option value="a_verifier">À vérifier</option>
                <option value="obsolete">Obsolète</option>
                <option value="indisponible">Indisponible</option>
                <option value="exclue">Exclue</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-[#6B6B6E] mb-1">Taux résolution min.</label>
              <input value={videoFilters.min_resolution} onChange={(e) => setVideoFilters((f) => ({ ...f, min_resolution: e.target.value }))}
                className="bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-[#FF6B35] focus:outline-none w-24"
                data-testid="vperf-filter-minres" placeholder="0.0 - 1.0" />
            </div>
            <button onClick={loadVideoPerf} className="px-3 py-2 text-xs bg-[#FF6B35] text-white rounded-lg flex items-center gap-1.5" data-testid="vperf-apply"><RefreshCw className="h-3.5 w-3.5" /> Appliquer</button>
          </div>

          {(!videoPerf || videoPerf.items.length === 0) && (
            <p className="text-center text-[#6B6B6E] text-sm py-8">Aucune donnée de performance vidéo pour l'instant. Les statistiques apparaissent dès que des clients valident des vidéos.</p>
          )}
          {videoPerf && videoPerf.items.map((v) => (
            <div key={v.video_id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl overflow-hidden" data-testid="vperf-video-row">
              <div className="flex items-start gap-3 p-3 border-b border-[#3A3A3D]">
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-20 h-12 rounded object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{v.title || v.video_id}</p>
                  <p className="text-[#6B6B6E] text-xs">{v.channel_title} · {v.status || 'proposee'}{v.maybe_obsolete ? ' · ⚠ potentiellement obsolète' : ''}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => videoPerfAction(v.video_id, 'check_availability')} className="p-1.5 rounded-lg text-[#00FFFF] hover:bg-[#00FFFF]/10" title="Vérifier la disponibilité" data-testid="vperf-check"><RefreshCw className="h-4 w-4" /></button>
                  <button onClick={() => videoPerfAction(v.video_id, 'mark_obsolete')} className="p-1.5 rounded-lg text-[#FF6B35] hover:bg-[#FF6B35]/10" title="Marquer obsolète" data-testid="vperf-obsolete"><Ban className="h-4 w-4" /></button>
                  <button onClick={() => videoPerfAction(v.video_id, 'exclude')} className="p-1.5 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Exclure" data-testid="vperf-exclude"><Trash2 className="h-4 w-4" /></button>
                  <button onClick={() => videoPerfAction(v.video_id, 'reset')} className="p-1.5 rounded-lg text-[#B8B8BA] hover:bg-[#B8B8BA]/10" title="Réinitialiser" data-testid="vperf-reset"><RefreshCw className="h-4 w-4 rotate-180" /></button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#111113] text-[#6B6B6E]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Appareil</th>
                      <th className="text-left px-3 py-2 font-medium">App</th>
                      <th className="text-left px-3 py-2 font-medium">Problème</th>
                      <th className="text-right px-3 py-2 font-medium">Réponses</th>
                      <th className="text-right px-3 py-2 font-medium">Appareil ✓</th>
                      <th className="text-right px-3 py-2 font-medium">App ✓</th>
                      <th className="text-right px-3 py-2 font-medium">Résolu</th>
                      <th className="text-right px-3 py-2 font-medium">Partiel</th>
                      <th className="text-right px-3 py-2 font-medium">Échec</th>
                      <th className="text-right px-3 py-2 font-medium">Menus diff.</th>
                      <th className="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.contexts.map((c) => (
                      <tr key={c.context_key} className="border-t border-[#3A3A3D]" data-testid="vperf-context-row">
                        <td className="px-3 py-2 text-[#E8E8E8]">{c.device || '—'}{c.downgraded ? ' 🔻' : ''}{c.favored ? ' ⭐' : ''}</td>
                        <td className="px-3 py-2 text-[#B8B8BA]">{c.application || '—'}</td>
                        <td className="px-3 py-2 text-[#B8B8BA]">{c.problem || '—'}</td>
                        <td className="px-3 py-2 text-right text-[#B8B8BA]">{c.responses}</td>
                        <td className="px-3 py-2 text-right text-[#00FF88]">{Math.round(c.device_match_rate * 100)}%</td>
                        <td className="px-3 py-2 text-right text-[#00FF88]">{Math.round(c.app_match_rate * 100)}%</td>
                        <td className="px-3 py-2 text-right text-[#00FF88]">{Math.round(c.resolution_rate * 100)}%</td>
                        <td className="px-3 py-2 text-right text-[#FFB800]">{Math.round(c.partial_rate * 100)}%</td>
                        <td className="px-3 py-2 text-right text-[#FF3B5C]">{Math.round(c.failure_rate * 100)}%</td>
                        <td className="px-3 py-2 text-right text-[#FF6B35]">{c.different_menus}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => videoPerfAction(v.video_id, 'favor', c.context_key)} className="p-1 rounded text-[#00FF88] hover:bg-[#00FF88]/10" title="Prioriser pour ce contexte" data-testid="vperf-favor"><Star className="h-3.5 w-3.5" /></button>
                            <button onClick={() => videoPerfAction(v.video_id, 'downgrade', c.context_key)} className="p-1 rounded text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Rétrograder pour ce contexte" data-testid="vperf-downgrade"><ThumbsDown className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}



      {tab === 'researched' && researched && (
        <div className="space-y-4" data-testid="researched-answers-panel">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <StatCard label="Total" value={researched.stats.total} color="#00FFFF" />
            <StatCard label="Générées" value={researched.stats.generated} color="#FFB800" />
            <StatCard label="À vérifier" value={researched.stats.awaiting_review} color="#FF6B35" />
            <StatCard label="Validées" value={researched.stats.validated} color="#00FF88" />
            <StatCard label="Publiées" value={researched.stats.published} color="#00FF88" />
            <StatCard label="Taux résolution" value={`${Math.round((researched.stats.resolution_rate || 0) * 100)}%`} color="#00FFFF" />
          </div>

          <div className="flex flex-wrap items-center gap-2 bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-3">
            <span className="text-xs text-[#6B6B6E]">Recherche Web :
              <span className={researched.web?.enabled ? 'text-[#00FF88] ml-1' : 'text-[#6B6B6E] ml-1'}>
                {researched.web?.enabled ? `activée (${researched.web.provider})` : 'désactivée (sources internes + IA)'}</span>
            </span>
            <div className="flex-1" />
            <select value={researchStatus} onChange={(e) => setResearchStatus(e.target.value)}
              className="bg-[#0A0A0B] border border-[#3A3A3D] rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-[#FF6B35] focus:outline-none" data-testid="research-filter-status">
              <option value="">Tous les statuts</option>
              <option value="generated">Générées</option>
              <option value="awaiting_review">À vérifier</option>
              <option value="validated">Validées</option>
              <option value="corrected">Corrigées</option>
              <option value="rejected">Rejetées</option>
              <option value="published_to_kb">Publiées</option>
            </select>
          </div>

          {researched.items.length === 0 && (
            <p className="text-center text-[#6B6B6E] text-sm py-8">Aucune réponse IA en attente. Elles apparaissent quand Nova doit chercher une réponse absente de la base.</p>
          )}
          {researched.items.map((a) => (
            <div key={a.id} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl p-4 space-y-3" data-testid="research-answer-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{a.question}</p>
                  <p className="text-[#6B6B6E] text-xs mt-1">
                    {[a.device_family, a.operating_system, a.application_name, a.problem].filter(Boolean).join(' · ') || '—'}
                    {a.occurrence_count > 1 && <span className="text-[#FF6B35]"> · {a.occurrence_count} cas similaires</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                    a.confidence_score === 'high' ? 'bg-[#00FF88]/15 text-[#00FF88]' :
                    a.confidence_score === 'medium' ? 'bg-[#FFB800]/15 text-[#FFB800]' :
                    'bg-[#FF3B5C]/15 text-[#FF3B5C]'}`}>confiance {a.confidence_score}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#3A3A3D] text-[#B8B8BA]">{a.admin_status}</span>
                </div>
              </div>
              {a.generated_answer && <p className="text-[#B8B8BA] text-sm">{a.generated_answer}</p>}
              {a.proposed_steps?.length > 0 && (
                <ol className="list-decimal list-inside space-y-1 text-[#E8E8E8] text-sm bg-[#111113] rounded-lg p-3">
                  {a.proposed_steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
              {a.sources?.length > 0 && (
                <p className="text-[#6B6B6E] text-xs">Sources : {a.sources.slice(0, 4).join(' · ')}</p>
              )}
              {a.problem_resolved !== null && a.problem_resolved !== undefined && (
                <p className="text-xs">Résultat client : <span className={a.problem_resolved ? 'text-[#00FF88]' : 'text-[#FF3B5C]'}>{a.problem_resolved ? 'résolu' : 'non résolu'}</span></p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={() => researchAction(a.id, 'validate')} className="text-xs px-3 py-1.5 rounded-lg bg-[#00FF88]/15 text-[#00FF88] hover:bg-[#00FF88]/25" data-testid="research-validate"><CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />Valider</button>
                <button onClick={() => researchPublish(a.id)} className="text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35]/15 text-[#FF6B35] hover:bg-[#FF6B35]/25" data-testid="research-publish"><BookOpen className="h-3.5 w-3.5 inline mr-1" />Publier dans la base</button>
                <button onClick={() => researchAction(a.id, 'reject')} className="text-xs px-3 py-1.5 rounded-lg bg-[#FF3B5C]/15 text-[#FF3B5C] hover:bg-[#FF3B5C]/25" data-testid="research-reject"><X className="h-3.5 w-3.5 inline mr-1" />Rejeter</button>
              </div>
            </div>
          ))}
        </div>
      )}


      {/* Connaissances à compléter (Phase 4.3-D) */}
      {tab === 'learning' && (
        <div className="space-y-6" data-testid="learning-tab">
          {learning.stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Demandes ouvertes" value={learning.stats.knowledge_requests_open} color="#FFB800" />
              <StatCard label="Demandes totales" value={learning.stats.knowledge_requests_total} />
              <StatCard label="Suggestions" value={learning.stats.suggestions_total} color="#00FFFF" />
              <StatCard label="Taux de publication" value={`${Math.round((learning.stats.suggestions_publication_rate || 0) * 100)}%`} color="#00FF88" />
            </div>
          )}

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="text-white font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#FF6B35]" /> Suggestions à officialiser (validation requise)</h3>
              <div className="flex gap-2">
                {selectedSug.length >= 2 && (
                  <button onClick={mergeSelected} data-testid="suggestions-merge-btn"
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40">
                    <GitMerge className="h-3.5 w-3.5" /> Fusionner ({selectedSug.length})
                  </button>
                )}
                <button onClick={runRescan} disabled={rescanning} data-testid="suggestions-rescan-btn"
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40">
                  {rescanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Analyser maintenant
                </button>
              </div>
            </div>
            {rescanReport && (
              <div className="mb-3 rounded-lg border border-[#3A3A3D] bg-[#111113] p-2.5 text-[11px] text-[#B8B8BA]" data-testid="rescan-report">
                Lancé à {new Date(rescanReport.started_at).toLocaleTimeString('fr-FR')} · durée {rescanReport.duration_s}s · collections : {(rescanReport.collections || []).join(', ')} · cas analysés {rescanReport.cases_analyzed} · créées {rescanReport.created} · fusionnées {rescanReport.merged} · ignorées {rescanReport.ignored}
                {(rescanReport.errors || []).length > 0 && <span className="text-[#FF3B5C]"> · erreurs : {rescanReport.errors.join('; ')}</span>}
              </div>
            )}
            <div className="space-y-2">
              {learning.suggestions.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucune suggestion pour le moment.</p>}
              {learning.suggestions.map((s) => (
                <div key={s.id} className={`bg-[#1A1A1D] border rounded-xl p-3 ${selectedSug.includes(s.id) ? 'border-[#00FFFF]' : 'border-[#3A3A3D]'}`} data-testid="learning-suggestion">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-2">
                      <input type="checkbox" checked={selectedSug.includes(s.id)} onChange={() => toggleSelectSug(s.id)}
                        className="mt-1" data-testid="suggestion-select" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium">{s.title}</p>
                        <p className="text-[#B8B8BA] text-xs mt-0.5">{s.description}</p>
                        <p className="text-[10px] text-[#6B6B6E] mt-1">Type : {s.type} · Cas : {s.affected ?? s.occurrences}{s.resolution_rate ? ` · Taux ${Math.round(s.resolution_rate*100)}%` : ''} · Contexte : {[s.device, s.application].filter(Boolean).join('/') || '—'} · Statut : <span className="text-[#00FFFF]">{s.status}</span>{s.merged_from ? ` · fusion de ${s.merged_from.length}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 gap-1">
                      <button onClick={() => genDraft(s.id)} className="p-2 rounded-lg text-[#FF6B35] hover:bg-[#FF6B35]/10" title="Générer un brouillon" data-testid="suggestion-generate-draft"><FilePlus2 className="h-4 w-4" /></button>
                      <button onClick={() => openGuided(s)} className="p-2 rounded-lg text-[#00FFFF] hover:bg-[#00FFFF]/10" title="Publier (flux guidé)" data-testid="suggestion-guided-publish"><Rocket className="h-4 w-4" /></button>
                      <button onClick={() => sugAction(s.id, 'under_review')} className="p-2 rounded-lg text-[#FFB800] hover:bg-[#FFB800]/10" title="Analyser" data-testid="suggestion-review"><Wand2 className="h-4 w-4" /></button>
                      <button onClick={() => sugAction(s.id, 'validated')} className="p-2 rounded-lg text-[#00FF88] hover:bg-[#00FF88]/10" title="Valider" data-testid="suggestion-validate"><CheckCircle2 className="h-4 w-4" /></button>
                      <button onClick={() => sugAction(s.id, 'postponed')} className="p-2 rounded-lg text-[#B8B8BA] hover:bg-white/10" title="Reporter" data-testid="suggestion-postpone"><Loader2 className="h-4 w-4" /></button>
                      <button onClick={() => sugAction(s.id, 'rejected')} className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Rejeter" data-testid="suggestion-reject"><Ban className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><MessageSquareWarning className="h-4 w-4 text-[#FFB800]" /> Demandes de connaissance (cas remontés)</h3>
            <div className="overflow-x-auto rounded-xl border border-[#3A3A3D]">
              <table className="w-full text-sm">
                <thead className="bg-[#1A1A1D] text-[#6B6B6E]">
                  <tr>
                    <th className="px-3 py-2 text-left">Question</th>
                    <th className="px-3 py-2 text-left">Appareil / App</th>
                    <th className="px-3 py-2 text-left">Raisons</th>
                    <th className="px-3 py-2 text-left">Conf.</th>
                    <th className="px-3 py-2 text-left">Clients</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {learning.requests.length === 0 && (
                    <tr><td colSpan="7" className="px-3 py-6 text-center text-[#6B6B6E]">Aucune demande.</td></tr>
                  )}
                  {learning.requests.map((k) => (
                    <tr key={k.id} className="border-t border-[#3A3A3D]" data-testid="knowledge-request-row">
                      <td className="px-3 py-2 text-[#E8E8E8] max-w-[220px] truncate">{k.question || '—'}</td>
                      <td className="px-3 py-2 text-[#B8B8BA]">{k.device || '?'} / {k.application || '?'}</td>
                      <td className="px-3 py-2 text-[#6B6B6E] text-xs max-w-[180px] truncate">{(k.reasons || []).join(', ')}</td>
                      <td className="px-3 py-2 text-[#B8B8BA]">{k.confidence_score}</td>
                      <td className="px-3 py-2 text-[#B8B8BA]">{k.affected_clients_count}</td>
                      <td className="px-3 py-2"><span className="text-[11px] px-2 py-0.5 rounded-full bg-[#00FFFF]/15 text-[#00FFFF]">{k.status}</span></td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => krAction(k.id, 'en_analyse')} className="p-2 rounded-lg text-[#FFB800] hover:bg-[#FFB800]/10" title="En analyse" data-testid="kr-analyze"><Wand2 className="h-4 w-4" /></button>
                          <button onClick={() => krAction(k.id, 'traitee')} className="p-2 rounded-lg text-[#00FF88] hover:bg-[#00FF88]/10" title="Traitée" data-testid="kr-resolve"><CheckCircle2 className="h-4 w-4" /></button>
                          <button onClick={() => krAction(k.id, 'rejetee')} className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Rejeter" data-testid="kr-reject"><Ban className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Historique des versions */}
      {tab === 'versions' && (
        <div className="space-y-4" data-testid="versions-tab">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-white font-semibold flex items-center gap-2"><History className="h-4 w-4 text-[#FF6B35]" /> Historique des versions</h3>
            <select value={verFilter.object_type} onChange={(e) => setVerFilter({ object_type: e.target.value })}
              className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-xs text-white" data-testid="versions-filter-type">
              <option value="">Tous les objets</option>
              <option value="procedure">Procédures</option>
              <option value="article">Fiches</option>
              <option value="setting">Réglages</option>
            </select>
            {verPick.v1 && verPick.v2 && (
              <button onClick={runDiff} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40" data-testid="versions-diff-btn">
                <GitCompare className="h-3.5 w-3.5" /> Comparer v{verPick.v1} ↔ v{verPick.v2}
              </button>
            )}
          </div>

          {verDiff && (
            <div className="rounded-xl border border-[#3A3A3D] bg-[#111113] p-3 text-xs text-[#B8B8BA] space-y-1" data-testid="versions-diff">
              <p className="text-white font-medium">Diff {verDiff.object_type} {verDiff.ref} : v{verDiff.v1} → v{verDiff.v2}</p>
              {verDiff.object_type === 'procedure' && (
                <>
                  {(verDiff.steps?.added?.length > 0) && <p className="text-[#00FF88]">Étapes ajoutées : {verDiff.steps.added.join(', ')}</p>}
                  {(verDiff.steps?.removed?.length > 0) && <p className="text-[#FF3B5C]">Étapes retirées : {verDiff.steps.removed.join(', ')}</p>}
                  {(verDiff.branches_changed?.length > 0) && <p>Branches modifiées : {verDiff.branches_changed.join(', ')}</p>}
                  {(verDiff.text_changed?.length > 0) && <p>Textes modifiés : {verDiff.text_changed.join(', ')}</p>}
                  {(verDiff.devices?.added?.length > 0 || verDiff.devices?.removed?.length > 0) && <p>Appareils : +{(verDiff.devices.added || []).join(',') || '—'} / -{(verDiff.devices.removed || []).join(',') || '—'}</p>}
                  {(verDiff.apps?.added?.length > 0 || verDiff.apps?.removed?.length > 0) && <p>Applications : +{(verDiff.apps.added || []).join(',') || '—'} / -{(verDiff.apps.removed || []).join(',') || '—'}</p>}
                  {verDiff.priority && <p>Priorité : {verDiff.priority.before} → {verDiff.priority.after}</p>}
                  {(verDiff.resolution_criteria_changed?.length > 0) && <p>Critères de résolution modifiés : {verDiff.resolution_criteria_changed.join(', ')}</p>}
                </>
              )}
              {verDiff.object_type === 'article' && (
                <>
                  {(verDiff.added?.length > 0) && <p className="text-[#00FF88]">Ajouts : {verDiff.added.join(', ')}</p>}
                  {(verDiff.removed?.length > 0) && <p className="text-[#FF3B5C]">Retraits : {verDiff.removed.join(', ')}</p>}
                  {(verDiff.modified || []).map((m, i) => <p key={i}>• {m.field}{m.added ? ` (+${(m.added||[]).join(',')})` : ''}</p>)}
                </>
              )}
              <button onClick={() => setVerDiff(null)} className="text-[10px] text-[#6B6B6E] hover:text-white mt-1">Fermer</button>
            </div>
          )}

          {verStats && (
            <div className="rounded-xl border border-[#3A3A3D] bg-[#111113] p-3 text-xs text-[#B8B8BA]" data-testid="versions-stats">
              <p className="text-white font-medium mb-1">Stats v{verStats.version} — {verStats.ref}</p>
              <p>Usages : {verStats.usages} · Résolution : {Math.round((verStats.resolution_rate||0)*100)}% · Échec : {Math.round((verStats.failure_rate||0)*100)}% · Abandon : {Math.round((verStats.abandon_rate||0)*100)}% · Étapes moy. : {verStats.avg_steps} · Durée moy. : {verStats.avg_duration_s}s · Changements : {verStats.procedure_changes}</p>
              <p className="text-[10px] text-[#FFB800] italic mt-1">{verStats.disclaimer}</p>
              <button onClick={() => setVerStats(null)} className="text-[10px] text-[#6B6B6E] hover:text-white mt-1">Fermer</button>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-[#3A3A3D]">
            <table className="w-full text-sm">
              <thead className="bg-[#1A1A1D] text-[#6B6B6E]"><tr>
                <th className="px-3 py-2 text-left">Objet</th><th className="px-3 py-2 text-left">Réf</th>
                <th className="px-3 py-2 text-left">Version</th><th className="px-3 py-2 text-left">Auteur</th>
                <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Origine</th>
                <th className="px-3 py-2 text-left">Commentaire</th><th className="px-3 py-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {versions.length === 0 && <tr><td colSpan="8" className="px-3 py-6 text-center text-[#6B6B6E]">Aucune version.</td></tr>}
                {versions.map((v) => (
                  <tr key={v.id} className="border-t border-[#3A3A3D]" data-testid="version-row">
                    <td className="px-3 py-2 text-[#B8B8BA]">{v.object_type}</td>
                    <td className="px-3 py-2 text-[#E8E8E8] max-w-[160px] truncate">{v.ref}</td>
                    <td className="px-3 py-2 text-white">{v.version != null ? `v${v.version}` : '—'}</td>
                    <td className="px-3 py-2 text-[#B8B8BA] text-xs">{v.author || '—'}</td>
                    <td className="px-3 py-2 text-[#6B6B6E] text-xs">{v.date ? new Date(v.date).toLocaleString('fr-FR') : '—'}</td>
                    <td className="px-3 py-2 text-[#6B6B6E] text-xs">{v.origin}</td>
                    <td className="px-3 py-2 text-[#6B6B6E] text-xs max-w-[180px] truncate">{v.comment || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {v.version != null && (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setVerPick((p) => ({ object_type: v.object_type, ref: v.ref, v1: p.ref === v.ref ? p.v1 || v.version : v.version, v2: p.ref === v.ref && p.v1 ? v.version : p.v2 }))}
                            className="p-1.5 rounded-lg text-[#00FFFF] hover:bg-[#00FFFF]/10" title="Sélectionner pour comparer" data-testid="version-pick"><GitCompare className="h-4 w-4" /></button>
                          <button onClick={() => loadVerStats(v.object_type, v.ref, v.version)} className="p-1.5 rounded-lg text-[#FFB800] hover:bg-[#FFB800]/10" title="Statistiques" data-testid="version-stats"><Gauge className="h-4 w-4" /></button>
                          <button onClick={() => restoreVersion(v.object_type, v.ref, v.version)} className="p-1.5 rounded-lg text-[#FF6B35] hover:bg-[#FF6B35]/10" title="Restaurer" data-testid="version-restore"><RotateCcw className="h-4 w-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {verPick.ref && <p className="text-[11px] text-[#6B6B6E]">Sélection : {verPick.ref} — v1={verPick.v1 || '?'} v2={verPick.v2 || '?'}</p>}
        </div>
      )}

      {/* Alertes de dégradation */}
      {tab === 'degradations' && (
        <div className="space-y-4" data-testid="degradations-tab">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-white font-semibold flex items-center gap-2"><Gauge className="h-4 w-4 text-[#FF6B35]" /> Alertes de dégradation des procédures</h3>
            <button onClick={runDegradationScan} disabled={scanning} data-testid="degradation-scan-btn"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40">
              {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Analyser maintenant
            </button>
          </div>

          {/* État du scan nocturne */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-[#3A3A3D] bg-[#111113] p-3" data-testid="degradation-job-status">
              <p className="text-white text-sm font-medium flex items-center gap-2 mb-2"><Clock className="h-4 w-4 text-[#00FFFF]" /> Supervision du scan</p>
              <p className="text-[11px] text-[#00FFFF] mb-2">Planification : tous les jours à 02:30 — Europe/Paris</p>
              {degJob ? (
                <div className="text-[11px] text-[#B8B8BA] space-y-0.5">
                  <p>Dernière exécution : <span className="text-white">{degJob.finished_at ? new Date(degJob.finished_at).toLocaleString('fr-FR') : '—'}</span>{degJob.duration_s != null ? ` (${degJob.duration_s}s)` : ''}</p>
                  <p>Prochaine exécution : <span className="text-white">{degJob.next_run ? new Date(degJob.next_run).toLocaleString('fr-FR') : '—'}</span></p>
                  <p>Statut : <span className="text-white">{degJob.error ? 'erreur' : (degJob.skipped ? 'ignoré (auto désactivé)' : 'ok')}</span> · verrou : <span className="text-white">{degJob.locked ? 'actif' : 'libre'}</span></p>
                  <p>Procédures analysées : <span className="text-white">{degJob.scanned ?? '—'}</span> · créées : {degJob.alerts_created ?? 0} · maj : {degJob.alerts_updated ?? 0} · ignorées (volume) : {degJob.skipped_low_volume ?? 0}{degJob.emails_sent != null ? ` · e-mails : ${degJob.emails_sent}` : ''}</p>
                  {degJob.error && <p className="text-[#FF3B5C]">Erreur : {degJob.error}</p>}
                </div>
              ) : <p className="text-[11px] text-[#6B6B6E]">Aucune exécution enregistrée pour l'instant.</p>}
            </div>

            {/* Préférences administrateur */}
            <div className="rounded-xl border border-[#3A3A3D] bg-[#111113] p-3" data-testid="degradation-prefs">
              <p className="text-white text-sm font-medium flex items-center gap-2 mb-2"><Bell className="h-4 w-4 text-[#FFB800]" /> Préférences de notification</p>
              {degPrefs ? (
                <div className="text-[11px] text-[#B8B8BA] space-y-2">
                  <label className="flex items-center gap-2" data-testid="pref-auto-scan">
                    <input type="checkbox" checked={!!degPrefs.auto_scan_enabled} onChange={(e) => savePrefs({ auto_scan_enabled: e.target.checked })} />
                    Scan automatique nocturne activé
                  </label>
                  <div className="flex items-center gap-2">
                    <span>Mode :</span>
                    <select value={degPrefs.mode} onChange={(e) => savePrefs({ mode: e.target.value })}
                      className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-white" data-testid="pref-mode">
                      <option value="dashboard">Tableau de bord</option>
                      <option value="dashboard_email">Tableau de bord + e-mail</option>
                      <option value="critical_only">Alertes critiques uniquement</option>
                      <option value="daily">Résumé quotidien</option>
                      <option value="weekly">Résumé hebdomadaire</option>
                      <option value="off">Désactivé</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>Ignorer (snooze) :</span>
                    {[[24, '24 h'], [7, '7 j'], [30, '30 j']].map(([d, lbl]) => (
                      <button key={d} onClick={() => savePrefs({ snooze_days: d })}
                        className="px-2 py-1 rounded-lg border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid={`pref-snooze-${d}`}>{lbl}</button>
                    ))}
                  </div>
                  {degPrefs.snooze_until && <p className="text-[10px] text-[#FFB800]"><BellOff className="h-3 w-3 inline" /> Notifications e-mail suspendues jusqu'au {new Date(degPrefs.snooze_until).toLocaleString('fr-FR')}</p>}
                </div>
              ) : <p className="text-[11px] text-[#6B6B6E]">Chargement…</p>}
            </div>
          </div>

          {/* Notifications administrateur */}
          {degNotifs.length > 0 && (
            <div className="rounded-xl border border-[#FFB800]/30 bg-[#FFB800]/5 p-3" data-testid="degradation-notifications">
              <p className="text-white text-sm font-medium flex items-center gap-2 mb-2"><Bell className="h-4 w-4 text-[#FFB800]" /> Notifications non lues ({degNotifs.length})</p>
              <div className="space-y-1.5">
                {degNotifs.map((n) => (
                  <div key={n.id} className="flex items-start justify-between gap-2 text-[11px] border-b border-[#3A3A3D]/40 pb-1.5" data-testid="degradation-notification">
                    <div className="min-w-0">
                      <p className="text-white">{n.title}</p>
                      <p className="text-[10px] text-[#B8B8BA]">{n.procedure_slug} v{n.version} · {Math.round((n.previous_rate || 0) * 100)}% → {Math.round((n.current_rate || 0) * 100)}% (baisse {Math.round((n.drop || 0) * 100)}%, {n.cases} cas / {n.window_days}j) · {n.created_at ? new Date(n.created_at).toLocaleString('fr-FR') : ''}</p>
                    </div>
                    <button onClick={() => markNotifRead(n.id)} className="flex-shrink-0 text-[10px] px-2 py-1 rounded-lg border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid="notif-mark-read">Marquer comme lue</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {scanReport && (
            <div className="rounded-lg border border-[#3A3A3D] bg-[#111113] p-2.5 text-[11px] text-[#B8B8BA]" data-testid="degradation-report">
              Procédures analysées : {scanReport.scanned} · alertes créées : {scanReport.alerts_created} · mises à jour : {scanReport.alerts_updated} · ignorées (volume faible) : {scanReport.skipped_low_volume} · seuils : min {scanReport.thresholds?.min_cases} cas / baisse {Math.round((scanReport.thresholds?.drop_threshold||0)*100)}% / fenêtre {scanReport.thresholds?.window_days}j
            </div>
          )}
          <div className="space-y-2">
            {alerts.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucune alerte active. Les seuils protègent contre les faux positifs (volume faible ignoré).</p>}
            {alerts.map((a) => (
              <div key={a.id} className="bg-[#1A1A1D] border border-[#FF3B5C]/30 rounded-xl p-3" data-testid="degradation-alert">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">{a.procedure_title || a.procedure_slug} <span className="text-[#6B6B6E]">v{a.version}</span></p>
                    <p className="text-xs text-[#B8B8BA] mt-0.5">Taux : {Math.round((a.previous_rate||0)*100)}% → <span className="text-[#FF3B5C]">{Math.round((a.current_rate||0)*100)}%</span> (baisse {Math.round((a.drop||0)*100)}%) · {a.cases_current} cas sur {a.window_days}j</p>
                    <p className="text-[10px] text-[#6B6B6E] mt-0.5">Échec {Math.round((a.current_failure_rate||0)*100)}% · abandon {Math.round((a.current_abandon_rate||0)*100)}% · étapes moy {a.avg_steps} · boucles {a.loops} · « ne marche pas » {a.not_working_signals} · appareils {(a.devices||[]).join(',')||'—'}</p>
                  </div>
                  <div className="flex flex-shrink-0 gap-1">
                    <button onClick={() => loadVerStats('procedure', a.procedure_slug, a.version)} className="p-2 rounded-lg text-[#FFB800] hover:bg-[#FFB800]/10" title="Analyser les cas" data-testid="alert-analyze"><Gauge className="h-4 w-4" /></button>
                    <button onClick={() => restoreFromAlert(a)} className="p-2 rounded-lg text-[#FF6B35] hover:bg-[#FF6B35]/10" title="Restaurer l'ancienne version" data-testid="alert-restore"><RotateCcw className="h-4 w-4" /></button>
                    <button onClick={() => updateAlert(a.id, 'snoozed')} className="p-2 rounded-lg text-[#B8B8BA] hover:bg-white/10" title="Ignorer temporairement" data-testid="alert-snooze"><Loader2 className="h-4 w-4" /></button>
                    <button onClick={() => updateAlert(a.id, 'closed')} className="p-2 rounded-lg text-[#00FF88] hover:bg-[#00FF88]/10" title="Clôturer" data-testid="alert-close"><CheckCircle2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {verStats && (
            <div className="rounded-xl border border-[#3A3A3D] bg-[#111113] p-3 text-xs text-[#B8B8BA]">
              <p className="text-white font-medium mb-1">Stats v{verStats.version} — {verStats.ref}</p>
              <p>Usages : {verStats.usages} · Résolution : {Math.round((verStats.resolution_rate||0)*100)}% · Échec : {Math.round((verStats.failure_rate||0)*100)}% · Abandon : {Math.round((verStats.abandon_rate||0)*100)}%</p>
              <p className="text-[10px] text-[#FFB800] italic mt-1">{verStats.disclaimer}</p>
            </div>
          )}
        </div>
      )}



      {/* Procédures (Phase 4.3-E) */}
      {tab === 'procedures' && (
        <div className="space-y-4" data-testid="procedures-tab">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-semibold flex items-center gap-2"><BookOpen className="h-4 w-4 text-[#FF6B35]" /> Procédures conditionnelles</h3>
            <button onClick={newProc} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#FF6B35]/15 border border-[#FF6B35]/40 text-[#FF6B35]" data-testid="proc-new"><Plus className="h-4 w-4" /> Nouvelle procédure</button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-[#3A3A3D]">
            <table className="w-full text-sm">
              <thead className="bg-[#1A1A1D] text-[#6B6B6E]"><tr>
                <th className="px-3 py-2 text-left">Titre</th><th className="px-3 py-2 text-left">Appareils / Apps</th>
                <th className="px-3 py-2 text-left">Étapes</th><th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">v</th><th className="px-3 py-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {procedures.map((p) => (
                  <tr key={p.slug} className="border-t border-[#3A3A3D]" data-testid="procedure-row">
                    <td className="px-3 py-2 text-[#E8E8E8] max-w-[200px] truncate">{p.title}</td>
                    <td className="px-3 py-2 text-[#B8B8BA] text-xs">{(p.devices || []).join(', ') || '—'} / {(p.apps || []).join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-[#B8B8BA]">{p.steps_count}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${p.status === 'publie' ? 'bg-[#00FF88]/15 text-[#00FF88]' : 'bg-[#FFB800]/15 text-[#FFB800]'}`}>{p.status}</span></td>
                    <td className="px-3 py-2 text-[#6B6B6E]">{p.version || 0}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditorSlug(p.slug)} className="p-2 rounded-lg text-[#00FFFF] hover:bg-[#00FFFF]/10" title="Éditer" data-testid="proc-edit"><Wand2 className="h-4 w-4" /></button>
                        {p.status !== 'publie'
                          ? <button onClick={() => procStatus(p.slug, 'publie')} className="p-2 rounded-lg text-[#00FF88] hover:bg-[#00FF88]/10" title="Publier" data-testid="proc-row-publish"><Rocket className="h-4 w-4" /></button>
                          : <button onClick={() => procStatus(p.slug, 'desactive')} className="p-2 rounded-lg text-[#FFB800] hover:bg-[#FFB800]/10" title="Désactiver" data-testid="proc-row-disable"><Ban className="h-4 w-4" /></button>}
                        <button onClick={() => deleteProc(p.slug)} className="p-2 rounded-lg text-[#FF3B5C] hover:bg-[#FF3B5C]/10" title="Supprimer" data-testid="proc-delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {procedures.length === 0 && <tr><td colSpan="6" className="px-3 py-6 text-center text-[#6B6B6E]">Aucune procédure.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dépannages résolus / non résolus (Phase 4.3-E) */}
      {(tab === 'resolved' || tab === 'unresolved-ts') && (
        <div className="space-y-3" data-testid="troubleshootings-tab">
          <h3 className="text-white font-semibold">{tab === 'resolved' ? 'Dépannages résolus' : 'Dépannages non résolus'}</h3>
          <div className="overflow-x-auto rounded-xl border border-[#3A3A3D]">
            <table className="w-full text-sm">
              <thead className="bg-[#1A1A1D] text-[#6B6B6E]"><tr>
                <th className="px-3 py-2 text-left">Question / Problème</th><th className="px-3 py-2 text-left">Appareil / App</th>
                <th className="px-3 py-2 text-left">Procédure</th><th className="px-3 py-2 text-left">Étapes</th>
                {tab === 'resolved' && <th className="px-3 py-2 text-left">Tech.</th>}
                <th className="px-3 py-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {(tab === 'resolved' ? resolvedTs : unresolvedTs).map((t) => (
                  <tr key={t.id} className="border-t border-[#3A3A3D]" data-testid="troubleshooting-row">
                    <td className="px-3 py-2 text-[#E8E8E8] max-w-[220px] truncate">{t.problem_category || '—'}<span className="block text-[10px] text-[#6B6B6E] truncate">{t.question}</span></td>
                    <td className="px-3 py-2 text-[#B8B8BA]">{t.device || '?'} / {t.application || '?'}</td>
                    <td className="px-3 py-2 text-[#6B6B6E] text-xs">{t.procedure_slug || '—'}</td>
                    <td className="px-3 py-2 text-[#B8B8BA]">{t.steps_count}</td>
                    {tab === 'resolved' && <td className="px-3 py-2">{t.resolution_confirmed_technically ? <CheckCircle2 className="h-4 w-4 text-[#00FF88]" /> : <span className="text-[#6B6B6E]">—</span>}</td>}
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => procFromSession(t.id)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#FF6B35] hover:border-[#FF6B35]" data-testid="ts-create-proc"><FilePlus2 className="h-3.5 w-3.5" /> Créer une procédure</button>
                    </td>
                  </tr>
                ))}
                {(tab === 'resolved' ? resolvedTs : unresolvedTs).length === 0 && <tr><td colSpan="6" className="px-3 py-6 text-center text-[#6B6B6E]">Aucun dépannage.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editorSlug && <KbProcedureEditor slug={editorSlug} onClose={() => { setEditorSlug(null); loadProcedures(); }} />}

      {/* Flux guidé suggestion → publication */}
      {guided && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="guided-publish-modal">
          <div className="bg-[#111113] border border-[#3A3A3D] rounded-2xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2"><Rocket className="h-4 w-4 text-[#00FFFF]" /> Publication guidée — étape {guided.step}/4</h3>
              <button onClick={() => setGuided(null)} className="text-[#6B6B6E] hover:text-white" data-testid="guided-close"><X className="h-4 w-4" /></button>
            </div>
            {guided.step === 1 && (
              <div className="space-y-2 text-sm text-[#B8B8BA]" data-testid="guided-step-1">
                <p className="text-white font-medium">1. Résumé de la suggestion</p>
                <p>{guided.sug.title}</p>
                {guided.ctx ? (
                  <div className="text-xs space-y-1">
                    <p>Raison : {guided.ctx.reason}</p>
                    <p>Cas concernés : {guided.ctx.cases} · échecs : {guided.ctx.failures} · taux : {guided.ctx.resolution_rate != null ? Math.round(guided.ctx.resolution_rate*100)+'%' : '—'}</p>
                    <p>Contexte : {[guided.ctx.context?.device, guided.ctx.context?.application, guided.ctx.context?.problem].filter(Boolean).join(' / ') || '—'}</p>
                  </div>
                ) : <p className="text-xs text-[#6B6B6E]">Chargement du contexte…</p>}
                <button onClick={() => setGuided((g) => ({ ...g, step: 2 }))} className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white" data-testid="guided-next-1">Continuer</button>
              </div>
            )}
            {guided.step === 2 && (
              <div className="space-y-2 text-sm text-[#B8B8BA]" data-testid="guided-step-2">
                <p className="text-white font-medium">2. Générer le brouillon (IA + sources internes)</p>
                <p className="text-xs">Nova va préparer un brouillon à partir des cas résolus/non résolus et des connaissances existantes. Aucune publication automatique.</p>
                <button onClick={guidedGenerate} disabled={guided.loading} className="text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40" data-testid="guided-generate">
                  {guided.loading ? 'Génération…' : 'Générer le brouillon'}
                </button>
              </div>
            )}
            {guided.step === 3 && (
              <div className="space-y-2 text-sm text-[#B8B8BA]" data-testid="guided-step-3">
                <p className="text-white font-medium">3. Brouillon créé — diff & test</p>
                <p className="text-xs">Brouillon enregistré ({guided.draft?.suggestion_type}). Ouvrez l'éditeur pour ajuster, comparer et tester, puis publiez.</p>
                <div className="flex gap-2">
                  {guided.draft?.procedure?.slug && (
                    <button onClick={() => { setEditorSlug(guided.draft.procedure.slug); }} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white" data-testid="guided-open-editor">Ouvrir l'éditeur</button>
                  )}
                  <button onClick={() => setGuided((g) => ({ ...g, step: 4 }))} className="text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white" data-testid="guided-next-3">Publier</button>
                </div>
              </div>
            )}
            {guided.step === 4 && (
              <div className="space-y-2 text-sm text-[#B8B8BA]" data-testid="guided-step-4">
                <p className="text-white font-medium">4. Publication versionnée</p>
                <p className="text-xs">La publication crée une version (suggestion source tracée) et marque la suggestion « publiée » (jamais supprimée). La procédure invalide est bloquée.</p>
                <div className="flex gap-2">
                  <button onClick={guidedPublish} className="text-xs px-3 py-1.5 rounded-lg bg-[#00FF88]/20 text-[#00FF88] border border-[#00FF88]/40" data-testid="guided-publish-btn">Publier maintenant</button>
                  <button onClick={() => setGuided(null)} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA]">Annuler</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {modal && (
        <ArticleModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            loadArticles(); loadStats();
            if (modal.unresolvedId) loadUnresolved();
          }}
        />
      )}
      {profileModal && (
        <ProfileModal
          initial={profileModal}
          onClose={() => setProfileModal(null)}
          onSaved={() => { setProfileModal(null); loadProfiles(); }}
        />
      )}
    </div>
  );
};

export default AssistantAdmin;
